//TODO:
// This component, like it's report counterpart, needs serious work.
// memoizing and callbacking would help but it'd be a nightmare to change atm.
// I changed a few, but I'm leaving it be for right now while it works and is stable - HW

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { VsiBubbleFavs } from "../styled/App.styled";
import { Spinner } from "reactstrap";
import Stockchart from "../components/Stockchart";
import {
  formatDualAxisStockChartConfig,
  formatStockChartConfig,
  capitalizeWord
} from "../utils/Helpers";

import { useVsiAppContext } from "../stores/VsiApp";
import {
  enterpriseTrendColors,
  genericColors,
  iopsLineColor,
  latencyLineColor,
  mbsecLineColor
} from "../utils/chartUtils";

const chartCapacityMetrics = [
  { value: "all_history", label: "All History", route: "device_trends_all_history_proc" },
  { value: "all_nodes", label: "All Nodes", route: "device_trends_all_nodes_proc" },
  { value: "all_pools", label: "All Pools", route: "device_trends_all_pools_proc" },
  { value: "by_node", label: "By Node", route: "device_trends_by_node_proc" },
  { value: "by_volume", label: "By Volume", route: "device_trends_by_volume_proc" },
  { value: "by_pool", label: "By Pool", route: "device_trends_by_pool_proc" }
];

const allMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "total_free", label: "Free" },
  { value: "total_allocated", label: "Allocated" },
  { value: "usable", label: "Capacity" },
  { value: "iops", label: "IOPs" },
  { value: "latency", label: "Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "mbsec", label: "MBSec" }
];

const allPoolsMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "total_free", label: "Free" },
  { value: "allocated", label: "Allocated" },
  { value: "capacity", label: "Capacity" },
  { value: "iops", label: "IOPs" },
  { value: "latency", label: "Latency" },
  { value: "write_latency", label: "Write Latency" },
  { value: "mbsec", label: "MBSec" }
];

const historyMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "phys_used", label: "Physical Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "latency", label: "Latency" },
  { value: "mbsec", label: "MBSec" },
  { value: "drr", label: "Data Reduction Ratio" }
];

const nodeMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "latency", label: "Latency" },
  { value: "mbsec", label: "MBSec" }
];

const poolsMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "latency", label: "Latency" },
  { value: "mbsec", label: "MBSec" }
];

const volMetricTypes = [
  { value: "none", label: "None" },
  { value: "used", label: "Used (GiB)" },
  { value: "iops", label: "IOPs" },
  { value: "latency", label: "Latency" }
];

export default function DeviceTrends({ params }) {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("Device");

  const searchParams = useMemo(() => {
    return (params || []).reduce((params, { param, value }) => {
      params[param] = value;
      return params;
    }, {});
  }, [params]);

  const {
    metric: searchMetric,
    device_name: searchDeviceName,
    axis_1: searchAxis1,
    axis_2: searchAxis2,
    node: searchNode,
    volume: searchVolume,
    pool: searchPool
  } = searchParams;


  const defaultMetric = useMemo(() => chartCapacityMetrics.find(({ value }) => (searchMetric || "capacity") === value) || chartCapacityMetrics[0], [searchMetric]);
  const additionalChartOptionLabel = capitalizeWord(searchMetric?.split("_")[1]) || "";


  const route = useMemo(() => {
    let routeToSearch = `/api/v6/report/${encodeURI(defaultMetric.route)}?client_id=${clientId}&device_name=${encodeURI(searchDeviceName)}&period_id=${periodId}`;
    if (additionalChartOptionLabel.toLowerCase() === "pool" && searchPool) {
      routeToSearch += `&pool_name=${encodeURI(searchPool)}`;
    }
    if (additionalChartOptionLabel.toLowerCase() === "node" && searchNode) {
      routeToSearch += `&node_name=${encodeURI(searchNode)}`;
    }
    if (additionalChartOptionLabel.toLowerCase() === "volume" && searchVolume) {
      routeToSearch += `&volume_name=${encodeURI(searchVolume)}`;
    }
    return routeToSearch;
  }, [defaultMetric.route, searchDeviceName, searchNode, searchPool, searchVolume, clientId, periodId, additionalChartOptionLabel]);

  const [{ recordsets: data }, { isLoading }] = useDataWithRoute(route);

  const { axis1Type, axis2Type } = useMemo(() => {
    let metric = searchMetric?.split("_")[1];

    let metricType;
    if (metric === "node") {
      metricType = nodeMetricTypes;
    } else if (metric === "volume") {
      metricType = volMetricTypes;
    } else if (metric === "pool") {
      metricType = poolsMetricTypes;
    } else if (metric === "history") {
      metricType = historyMetricTypes;
    } else if (metric === "pools") {
      metricType = allPoolsMetricTypes;
    } else {
      metricType = allMetricTypes;
    }
    const axis1 = searchAxis1 || metricType[1].value;
    const axis2 = searchAxis2 || metricType[0].value;
    const defaultAxis1 = metricType.find(({ value }) => axis1 === value) || metricType[1];
    const defaultAxis2 = metricType.find(({ value }) => axis2 === value) || metricType[0];

    return {
      axis1Type: defaultAxis1,
      axis2Type: defaultAxis2,
    }

  }, [searchAxis1, searchAxis2, searchMetric]);

  const chartConfigs = useMemo(() => {
    let returnFields = [];
    const axes = [axis1Type, axis2Type];
    for (let i = 0; i <= 1; i++) {
      switch (axes[i].value) {
        case "used":
          let usedMetric = searchMetric.split("_")[1];
          if (usedMetric.includes("node")) {
            returnFields.push([
              {
                name: "Free",
                field: "total_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Capacity",
                field: "usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }

              }
            ]);
          } else if (usedMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Free",
                field: "free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else if (usedMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Free",
                field: "total_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Capacity",
                field: "capacity",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Free",
                field: "total_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Used",
                field: "total_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Capacity",
                field: "usable_gb",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Orphaned",
                field: "orphan_gb",
                yAxis: i,
                color: enterpriseTrendColors[4],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          ;
          break;
        case "phys_used":
          let physUsedMetric = searchMetric.split("_")[1];
          if (physUsedMetric.includes("node")) {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }

              }
            ]);
          } else if (physUsedMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }

              }
            ]);
          } else if (physUsedMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Physical Free",
                field: "physical_free",
                yAxis: i,
                color: enterpriseTrendColors[0],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }, {
                name: "Physical Used",
                field: "physical_used",
                yAxis: i,
                color: enterpriseTrendColors[1],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Allocated",
                field: "total_allocated",
                yAxis: i,
                color: enterpriseTrendColors[2],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Physical Capacity",
                field: "physical_usable",
                yAxis: i,
                color: enterpriseTrendColors[3],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              },
              {
                name: "Orphaned",
                field: "orphan_gb",
                yAxis: i,
                color: enterpriseTrendColors[4],
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          ;
          break;
        case "iops":
          let iopsMetric = searchMetric.split("_")[1];
          if (iopsMetric.includes("volume")) {
            returnFields.push([
              {
                name: "IOPs",
                field: "volume_iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "IOPs",
                field: "iops",
                yAxis: i,
                color: iopsLineColor,
                dataLabels: { format: "{point.y:,.0f}" },
                tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
              }
            ]);
          }
          ;
          break;
        case "latency":
          let latMetric = searchMetric.split("_")[1];
          if (latMetric.includes("node")) {
            returnFields.push([
              {
                name: "Read Latency",
                field: "latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }, {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "ShortDashDotDot"
              }
            ]);
          } else if (latMetric.includes("pool")) {
            returnFields.push([
              {
                name: "Read Latency",
                field: "latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Write Latency",
                field: "write_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "ShortDashDotDot"
              }
            ]);
          } else if (latMetric.includes("volume")) {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "DashDot"
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Read Latency",
                field: "read_latency",
                yAxis: i,
                color: latencyLineColor
              }, {
                name: "Avg Latency",
                field: "avg_latency",
                yAxis: i,
                color: latencyLineColor,
                dashStyle: "Dot"
              }
            ]);
          }
          ;
          break;
        case "mbsec":
          returnFields.push([
            {
              name: "MB/Sec",
              field: "mbsec",
              yAxis: i,
              color: mbsecLineColor
            }
          ]);
          break;
        case "drr":
          let drrMetric = searchMetric.split("_")[1];
          if (drrMetric.includes("history")) {
            returnFields.push([
              {
                name: "Data Reduction Ratio",
                field: "data_reduction_ratio",
                yAxis: i,
                color: genericColors[6]
              }
            ]);
          } else {
            returnFields.push([
              {
                name: "Data Reduction Ratio",
                field: "drr",
                yAxis: i,
                color: genericColors[6]
              }
            ]);
          }
          break;
        default:
          break;
      }
    }

    return [].concat(...returnFields);
  }, [axis1Type, axis2Type, searchMetric]);

  const handleStateWithData = useMemo(() => {
    return (data) => {
      let chartConfig = null;
      let formattedChartData = null;
      let metric = searchMetric?.split("_")[1];
      let defaultChoice = searchNode || searchVolume || searchPool;
      let additionalOption = {
        value: defaultChoice,
        label: defaultChoice,
        route: `${metric}_name=${defaultChoice}`
      };
      let isParsing = true;

      switch (defaultMetric.value) {
        case "all_history": {
          const historyChartConfig = chartConfigs;
          const allHistoryConfig = formatDualAxisStockChartConfig(data?.[0] || [], historyChartConfig);

          chartConfig = {
            yAxis: [
              { title: { text: axis1Type.label } },
              { title: { text: axis2Type.label }, opposite: true }
            ],
            colors: historyChartConfig.map(({ color }) => color)
          };

          formattedChartData = allHistoryConfig.series;
          additionalOption = {};
          break;
        }

        case "all_pools": {
          let findPools = {};
          const foundPoolsData = data?.[0]?.map(datum => {
            if (!findPools[datum.pool_name]) findPools[datum.pool_name] = true;
            return datum;
          });

          const fieldsPool = Object.keys(findPools).map(pool => ({
            name: pool,
            field: axis1Type.value
          }));

          const allPoolsConfig = formatStockChartConfig(foundPoolsData || [], fieldsPool, "pool_name");

          chartConfig = {
            yAxis: [{ title: { text: axis1Type.label } }]
          };

          formattedChartData = allPoolsConfig.series;
          additionalOption = {};
          break;
        }

        case "all_nodes": {
          let findNodes = {};
          const foundNodesData = data?.[0]?.map(datum => {
            if (!findNodes[datum.node_name]) findNodes[datum.node_name] = true;
            return datum;
          });

          const fieldsNode = Object.keys(findNodes).map(node => ({
            name: node,
            field: axis1Type.value
          }));

          const allNodesConfig = formatStockChartConfig(foundNodesData || [], fieldsNode, "node_name");

          chartConfig = {
            yAxis: [{ title: { text: axis1Type.label } }]
          };

          formattedChartData = allNodesConfig.series;
          additionalOption = {};
          break;
        }

        case "by_node": {
          if (searchPool || searchVolume) {
            additionalOption = {};
          }

          const nodeSelections = data?.[1]?.map(datum => ({
            value: datum.node_name,
            label: datum.node_name,
            route: `node_name=${datum.node_name}`
          })) ?? [];

          const byNodeChartConfig = chartConfigs;
          const byNodeConfig = formatDualAxisStockChartConfig(data?.[0] || [], byNodeChartConfig);

          chartConfig = {
            yAxis: [
              { title: { text: axis1Type.label } },
              { title: { text: axis2Type.label }, opposite: true }
            ],
            colors: byNodeChartConfig.map(({ color }) => color)
          };

          if (Object.keys(additionalOption).length === 0) {
            additionalOption = nodeSelections[0];
          }

          formattedChartData = byNodeConfig.series;
          break;
        }

        case "by_volume": {
          if (searchPool || searchNode) {
            additionalOption = {};
          }

          const volSelections = data?.[1]?.map(datum => ({
            value: datum.volume_name,
            label: datum.volume_name,
            route: `volume_name=${datum.volume_name}`
          })) ?? [];

          const byVolumeChartConfig = chartConfigs;
          const byVolConfig = formatDualAxisStockChartConfig(data?.[0] || [], byVolumeChartConfig);

          chartConfig = {
            yAxis: [
              { title: { text: axis1Type.label } },
              { title: { text: axis2Type.label }, opposite: true }
            ],
            colors: byVolumeChartConfig.map(({ color }) => color)
          };

          if (Object.keys(additionalOption).length === 0) {
            additionalOption = volSelections[0];
          }

          formattedChartData = byVolConfig.series;
          break;
        }

        case "by_pool": {
          if (searchNode || searchVolume) {
            additionalOption = {};
          }

          const poolSelections = data?.[1]?.map(datum => ({
            value: datum.pool_name,
            label: datum.pool_name,
            route: `pool_name=${datum.pool_name}`
          })) ?? [];

          const byPoolChartConfig = chartConfigs;
          const byPoolConfig = formatDualAxisStockChartConfig(data?.[0] || [], byPoolChartConfig);

          chartConfig = {
            yAxis: [
              { title: { text: axis1Type.label } },
              { title: { text: axis2Type.label }, opposite: true }
            ],
            colors: byPoolChartConfig.map(({ color }) => color)
          };

          const poolInQuery = searchPool;
          if (Object.keys(additionalOption).length === 0) {
            additionalOption = poolSelections.find(pool => pool.value === decodeURIComponent(poolInQuery)) ?? poolSelections[0];
          }

          formattedChartData = byPoolConfig.series;
          break;
        }

        default:
          // Do nothing
          break;
      }

      isParsing = false;

      return {
        chartConfig,
        formattedChartData,
        additionalChartOption: additionalOption,
        isParsing
      };
    };
  }, [defaultMetric, searchNode, searchPool, searchVolume, axis1Type, axis2Type, chartConfigs, searchMetric]);

  const {
    chartConfig,
    formattedChartData,
    additionalChartOption,
  } = handleStateWithData(data);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubbleFavs unit={defaultMetric}
                   autobotInfo={{ secondaryMetric: [additionalChartOption.value], yAxes: [axis1Type, axis2Type] }}>
      <Stockchart series={formattedChartData} config={chartConfig} />
    </VsiBubbleFavs>
  );
}
